import { useEffect, useMemo, useState } from "react";
import { Card, ImagePlaceholder, Badge, Carousel } from "@hotelise/ui";
import { useIsMobile } from "@hotelise/ui";
import { ChevronRight } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import ContractVisual from "./ContractVisual";
import ContractsClient from "services/api/contracts";
import { getProperties } from "state/modules/properties";
import { getPropertiesList, getPropertiesLoaded } from "state/modules/properties/selectors";
import type { Property } from "models/properties";
import type { Contract } from "interfaces/contracts";

export default function AgreementsPage() {
	const isMobile = useIsMobile();
	const dispatch = useDispatch();
	const properties = useSelector(getPropertiesList);
	const propertiesLoaded = useSelector(getPropertiesLoaded);

	const [contracts, setContracts] = useState<Contract[]>([]);
	const [contractsLoaded, setContractsLoaded] = useState(false);
	const [selectedId, setSelectedId] = useState<string>("");

	useEffect(() => {
		document.title = "Agreements \u2014 Hotelise";
	}, []);

	useEffect(() => {
		if (!propertiesLoaded) {
			dispatch(getProperties());
		}
	}, [dispatch, propertiesLoaded]);

	useEffect(() => {
		ContractsClient.getOwnerContracts()
			.then((res) => {
				setContracts(res.data?.content || res.data?.items || res.data || []);
				setContractsLoaded(true);
			})
			.catch(() => {
				setContractsLoaded(true);
			});
	}, []);

	// Auto-select first property once loaded
	useEffect(() => {
		if (!selectedId && properties.length > 0) {
			setSelectedId(properties[0].id);
		}
	}, [properties, selectedId]);

	const contractByProperty = useMemo(() => {
		const map = new Map<string, Contract>();
		for (const c of contracts) {
			if (c.propertyId) {
				map.set(c.propertyId, c);
			}
		}
		return map;
	}, [contracts]);

	const selectedProperty = useMemo(
		() => properties.find((p) => p.id === selectedId) ?? properties[0],
		[properties, selectedId]
	);

	const selectedContract = selectedProperty
		? contractByProperty.get(selectedProperty.id)
		: undefined;

	if (!propertiesLoaded || !contractsLoaded) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="text-sm text-muted-foreground">Loading...</div>
			</div>
		);
	}

	if (properties.length === 0) {
		return (
			<div className="space-y-4">
				<div>
					<h1 className="text-2xl font-semibold">Agreements</h1>
					<p className="text-sm text-muted-foreground">No properties found.</p>
				</div>
			</div>
		);
	}

	const renderPropertyCard = (prop: Property, isMobileCard?: boolean) => {
		const contract = contractByProperty.get(prop.id);
		const fee = contract?.hoteliseFee;
		const vat = contract?.vat;

		return (
			<button
				key={prop.id}
				onClick={() => setSelectedId(prop.id)}
				className={`${isMobileCard ? "w-full" : "group"} text-left rounded-xl overflow-hidden border-2 transition-all ${
					!isMobileCard ? "hover:shadow-md" : ""
				} ${
					selectedId === prop.id
						? "border-primary shadow-md" + (!isMobileCard ? " ring-1 ring-primary/20" : "")
						: isMobileCard
						? "border-transparent"
						: "border-border"
				}`}
			>
				<ImagePlaceholder
					src={prop.picture}
					alt={prop.name}
					className={`w-full ${isMobileCard ? "h-32" : "h-40"} object-cover`}
					aspectRatio=""
				/>
				<div className={`${isMobileCard ? "p-3" : "p-4"} bg-card`}>
					<div className="flex items-center justify-between">
						<span className={`font-semibold ${isMobileCard ? "text-sm" : ""}`}>
							{prop.name}
						</span>
						<div className="flex items-center gap-1.5">
							{fee != null && (
								<Badge variant="secondary" className="tabular-nums text-xs">
									{fee}%
								</Badge>
							)}
							{vat != null && vat > 0 && (
								<span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">
									+{vat}% VAT
								</span>
							)}
							{!isMobileCard && (
								<ChevronRight
									className={`h-4 w-4 text-muted-foreground transition-transform ${
										selectedId === prop.id
											? "rotate-90"
											: "group-hover:translate-x-0.5"
									}`}
								/>
							)}
						</div>
					</div>
					<span className={`${isMobileCard ? "text-xs" : "text-sm"} text-muted-foreground line-clamp-1`}>
						{prop.address?.display || ""}
					</span>
				</div>
			</button>
		);
	};

	/* ── Mobile ──────────────────────────────────────────── */
	if (isMobile) {
		return (
			<div className="space-y-4">
				<div>
					<h1 className="text-xl font-semibold">Agreements</h1>
					<p className="text-sm text-muted-foreground">Your property contracts at a glance.</p>
				</div>

				<Carousel className="w-full -mx-1" slideClassName="flex-[0_0_75%] min-w-0">
					{properties.map((prop) => renderPropertyCard(prop, true))}
				</Carousel>

				{selectedContract ? (
					<ContractVisual contract={selectedContract} />
				) : (
					<div className="text-sm text-muted-foreground py-8 text-center">
						No contract found for this property.
					</div>
				)}
			</div>
		);
	}

	/* ── Desktop ─────────────────────────────────────────── */
	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-semibold">Agreements</h1>
				<p className="text-sm text-muted-foreground">Visual overview of your property contracts with Hotelise.</p>
			</div>

			{/* Property selector grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
				{properties.map((prop) => renderPropertyCard(prop))}
			</div>

			{/* Contract detail */}
			{selectedProperty && (
				<Card className="p-6">
					<div className="flex items-center gap-3 mb-4">
						<ImagePlaceholder
							src={selectedProperty.picture}
							alt={selectedProperty.name}
							className="h-12 w-12 rounded-lg object-cover"
							aspectRatio=""
							iconSize="h-5 w-5"
						/>
						<div>
							<h2 className="text-lg font-semibold">{selectedProperty.name}</h2>
							<p className="text-sm text-muted-foreground">{selectedProperty.address?.display || ""}</p>
						</div>
					</div>
					{selectedContract ? (
						<ContractVisual contract={selectedContract} />
					) : (
						<div className="text-sm text-muted-foreground py-8 text-center">
							No contract found for this property.
						</div>
					)}
				</Card>
			)}
		</div>
	);
}
